import { Link, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import {
  PageHeader, DashboardCard, StatusBadge, Button,
  Table, TableHeader, TableRow, TableHead, TableBody, TableCell,
} from '@zafroi/ui-kit';
import { useRateCard, useRateCardTransition } from '@/hooks/useCommercial';
import { QueryStateGuard } from '@/components/common/QueryStateGuard';
import { catalogStatusVariants } from '@/config/commercial-status';
import { CATALOG_ACTIONS } from '@/config/catalog-actions';
import { formatMoney, shortDate } from '@/lib/format';

const cap = (s: string) => s.charAt(0) + s.slice(1).toLowerCase();

export function RateCardDetailPage() {
  const { id } = useParams<{ id: string }>();
  const q = useRateCard(id);
  const transition = useRateCardTransition();
  return (
    <div className="space-y-6">
      <QueryStateGuard data={q.data} isLoading={q.isLoading} isError={q.isError}>
        {(x) => {
          const actions = CATALOG_ACTIONS[x.status] ?? [];
          const entries = x.entries ?? [];
          return (
            <>
              <PageHeader
                eyebrow={<Link to="/setup/rate-cards" className="inline-flex items-center gap-1 hover:text-foreground"><ArrowLeft className="h-3.5 w-3.5" /> Rate Cards</Link>}
                title={x.name}
                meta={<StatusBadge status={x.status} variants={catalogStatusVariants} />}
                actions={
                  <div className="flex gap-2">
                    {actions.map((a, i) => (
                      <Button key={a.to} size="sm" variant={i === 0 ? 'default' : 'outline'}
                        disabled={transition.isPending} onClick={() => id && transition.mutate({ id, to: a.to })}>{a.label}</Button>
                    ))}
                  </div>
                }
              />
              <DashboardCard title="Details">
                <div className="space-y-1.5 text-[13px]">
                  <Row k="Currency" v={x.currency} />
                  <Row k="Effective from" v={x.effectiveFrom ? shortDate(x.effectiveFrom) : '—'} />
                  <Row k="Effective to" v={x.effectiveTo ? shortDate(x.effectiveTo) : '—'} />
                </div>
              </DashboardCard>
              <DashboardCard title="Rates" flushBody>
                {entries.length === 0 ? (
                  <p className="p-4 text-sm text-muted-foreground">No rates.</p>
                ) : (
                  <Table>
                    <TableHeader><TableRow>
                      <TableHead>Role</TableHead><TableHead>Level</TableHead>
                      <TableHead>Unit</TableHead><TableHead className="text-right">Rate</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {entries.map((e, i) => (
                        <TableRow key={i}>
                          <TableCell className="font-medium">{e.role}</TableCell>
                          <TableCell>{e.level ?? '—'}</TableCell>
                          <TableCell>{cap(e.unit)}</TableCell>
                          <TableCell className="text-right tabular-nums">{formatMoney(e.rate, x.currency)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </DashboardCard>
            </>
          );
        }}
      </QueryStateGuard>
    </div>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return <div className="flex justify-between gap-4"><span className="text-muted-foreground">{k}</span><span className="text-right">{v}</span></div>;
}
